import React, { useMemo } from 'react';
import { useChordPlayer } from '../hooks/useChordPlayer';
import { chordToSpanish } from '../utils/singerFriendly';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import {
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Music,
  Guitar,
} from 'lucide-react';

/**
 * ChordPlayer — Transport bar for MIDI chord playback.
 * Renders play/pause, skip, BPM, volume, instrument toggle, and current chord badge.
 *
 * @param {Object} props
 * @param {string[]} props.chordSequence - Ordered chord symbols
 * @param {number} props.defaultBpm - Default tempo
 * @param {function} props.onChordChange - Callback(index) when active chord changes
 * @param {string} props.className - Additional CSS classes
 */
export function ChordPlayer({ chordSequence = [], defaultBpm = 100, onChordChange, className }) {
  const {
    isPlaying,
    isLoaded,
    currentIndex,
    play,
    pause,
    stop,
    skipForward,
    skipBackward,
    setBpm,
    setVolume,
    setInstrument,
    bpm,
    volume,
    instrument,
  } = useChordPlayer(chordSequence, { defaultBpm, onChordChange });

  const currentChord = currentIndex >= 0 && currentIndex < chordSequence.length
    ? chordSequence[currentIndex]
    : null;

  const currentChordSpanish = useMemo(
    () => currentChord ? chordToSpanish(currentChord) : '',
    [currentChord]
  );

  if (chordSequence.length === 0) return null;

  return (
    <div className={cn(
      'flex flex-wrap items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm',
      className
    )}>
      {/* Instrument toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={() => setInstrument(instrument === 'piano' ? 'guitar' : 'piano')}
        title={instrument === 'piano' ? 'Cambiar a guitarra' : 'Cambiar a piano'}
        disabled={!isLoaded}
      >
        {instrument === 'piano' ? <Music className="w-4 h-4" /> : <Guitar className="w-4 h-4" />}
      </Button>

      {/* Transport controls */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={skipBackward}
          disabled={!isLoaded || currentIndex <= 0}
          title="Acorde anterior"
        >
          <SkipBack className="w-4 h-4" />
        </Button>

        <Button
          variant="default"
          size="icon"
          className="h-9 w-9 rounded-full"
          onClick={isPlaying ? pause : play}
          disabled={!isLoaded}
          title={isPlaying ? 'Pausar' : 'Reproducir'}
        >
          {!isLoaded ? (
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={skipForward}
          disabled={!isLoaded || currentIndex >= chordSequence.length - 1}
          title="Siguiente acorde"
        >
          <SkipForward className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={stop}
          disabled={!isLoaded || currentIndex < 0}
          title="Detener"
        >
          <Square className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* BPM slider */}
      <div className="hidden sm:flex items-center gap-2 min-w-[120px]">
        <span className="text-xs text-muted-foreground whitespace-nowrap font-mono w-12 text-right">
          {bpm} BPM
        </span>
        <Slider
          value={[bpm]}
          min={40}
          max={200}
          step={5}
          onValueChange={([val]) => setBpm(val)}
          className="w-20"
        />
      </div>

      {/* Volume */}
      <div className="hidden sm:flex items-center gap-1.5 min-w-[80px]">
        <button
          onClick={() => setVolume(volume === 0 ? 0.7 : 0)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={volume === 0 ? 'Activar sonido' : 'Silenciar'}
        >
          {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <Slider
          value={[volume * 100]}
          min={0}
          max={100}
          step={5}
          onValueChange={([val]) => setVolume(val / 100)}
          className="w-16"
        />
      </div>

      {/* Current chord badge */}
      {currentChord && (
        <Badge
          variant="secondary"
          className="font-mono text-sm px-3 py-1 animate-pulse-once"
          title={currentChordSpanish}
        >
          {currentChord}
        </Badge>
      )}

      {/* Progress indicator */}
      <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
        {currentIndex >= 0 ? currentIndex + 1 : 0}/{chordSequence.length}
      </span>
    </div>
  );
}

export default ChordPlayer;
